import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, Form, Button, Alert, InputGroup, Row, Col, Container, Modal } from 'react-bootstrap';
import axios from 'axios';
import { Navigate, useSearchParams } from 'react-router-dom';
import tableDataModules from '../controller/tableDataModules';
import AuthCheck from '../components/AuthCheck';

function UserSettings() {
    const { register, handleSubmit, formState: { errors }, getValues, watch, setValue } = useForm();
    const [searchParams] = useSearchParams();
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [countryCode, setCountryCode] = useState('+31');
    const [isAdmin, setIsAdmin] = useState(false);
    const [targetUserId, setTargetUserId] = useState(null);
    const [show2FAModal, setShow2FAModal] = useState(false);
    const [qrCode, setQrCode] = useState('');
    const [twoFACode, setTwoFACode] = useState('');
    
    const password = watch('password');
    
    // Check if user is admin and if editing another user
    useEffect(() => {
        const userRole = localStorage.getItem("role");
        const adminUserId = searchParams.get('userId');
        
        setIsAdmin(userRole?.includes("ADMIN"));
        setTargetUserId(adminUserId);
        
        // Load user data
        loadUserData(adminUserId);
    }, [searchParams]);
    
    const loadUserData = (userId) => {
        setLoading(true);
        
        if (userId) {
            // Admin viewing another user - first get the user from the students list to find studentNumber
            tableDataModules.getStudents().then(response => {
                if (response?.data) {
                    const user = response.data.find(student => student.id.toString() === userId);
                    if (user) {
                        // Now get detailed user data using studentNumber
                        tableDataModules.getStudent(user.studentNumber)
                            .then(detailResponse => {
                                setUserInfo(detailResponse.data);
                                populateForm(detailResponse.data);
                                setLoading(false);
                            })
                            .catch(err => {
                                setError('Error loading user details: ' + (err.message || 'Unknown error'));
                                setLoading(false);
                            });
                    } else {
                        setError('User not found');
                        setLoading(false);
                    }
                } else {
                    setError('Error loading users list');
                    setLoading(false);
                }
            }).catch(err => {
                setError('Error loading user data: ' + (err.message || 'Unknown error'));
                setLoading(false);
            });
        } else {
            // User viewing their own settings - use their own ID to access their data
            const currentUserId = localStorage.getItem("id");
            
            tableDataModules.getStudent(currentUserId)
                .then(response => {
                    setUserInfo(response.data);
                    populateForm(response.data);
                    setLoading(false);
                })
                .catch(err => {
                    setError('Error loading user data: ' + (err.message || 'Unknown error'));
                    setLoading(false);
                });
        }
    };
    
    const populateForm = (user) => {
        setValue('firstName', user.firstName || '');
        setValue('lastName', user.lastName || '');
        setValue('year', user.year || '');
        setValue('email', user.email || '');
        setValue('fulltime', user.fulltime || false);
        
        // Parse phone number to extract country code and number
        if (user.phone) {
            // Try to match common country code patterns (+1 to +999)
            const match = user.phone.match(/^(\+\d{1,3})(.*)$/);
            if (match) {
                setCountryCode(match[1]);
                setValue('phoneNumber', match[2].replace(/\D/g, '')); // Remove any non-digits
            } else {
                // If no country code found, assume it's just the number
                setValue('phoneNumber', user.phone.replace(/\D/g, ''));
            }
        } else {
            setValue('phoneNumber', '');
        }
    };
    
    // Password validation functions (same as PasswordMigration.js)
    const validatePassword = (value) => {
        if (!value) return true; // Password is optional for updates
        
        const hasUpperCase = /[A-Z]/.test(value);
        const hasLowerCase = /[a-z]/.test(value);
        const hasNumbers = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
        const hasMinLength = value.length >= 8;
        
        if (!hasMinLength) return "Password must be at least 8 characters long";
        if (!hasUpperCase) return "Password must contain at least one uppercase letter";
        if (!hasLowerCase) return "Password must contain at least one lowercase letter";
        if (!hasNumbers) return "Password must contain at least one number";
        if (!hasSpecialChar) return "Password must contain at least one special character";
        return true;
    };
    
    const validateEmail = (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) || "Please enter a valid email address";
    };
    
    const validatePhone = (value) => {
        const phoneRegex = /^[0-9]{8,15}$/;
        return phoneRegex.test(value) || "Please enter a valid phone number (8-15 digits)";
    };
    
    // Password strength indicator
    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, text: '', color: '' };
        
        let score = 0;
        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[a-z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
        
        if (score <= 2) return { strength: score * 20, text: 'Weak', color: 'danger' };
        if (score <= 3) return { strength: score * 20, text: 'Fair', color: 'warning' };
        if (score <= 4) return { strength: score * 20, text: 'Good', color: 'info' };
        return { strength: score * 20, text: 'Strong', color: 'success' };
    };
    
    const passwordStrength = getPasswordStrength(password);
    
    const countryOptions = [
        { code: '+31', name: 'Netherlands', flag: '🇳🇱' },
        { code: '+1', name: 'United States', flag: '🇺🇸' },
        { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
        { code: '+49', name: 'Germany', flag: '🇩🇪' },
        { code: '+33', name: 'France', flag: '🇫🇷' },
        { code: '+32', name: 'Belgium', flag: '🇧🇪' },
        { code: '+34', name: 'Spain', flag: '🇪🇸' },
        { code: '+39', name: 'Italy', flag: '🇮🇹' },
        { code: '+46', name: 'Sweden', flag: '🇸🇪' },
        { code: '+47', name: 'Norway', flag: '🇳🇴' },
        { code: '+45', name: 'Denmark', flag: '🇩🇰' },
    ];
    
    const handleSetup2FA = () => {
        if (targetUserId && isAdmin) {
            // Admin setting up 2FA for another user
            const bodyFormData = new FormData();
            bodyFormData.append("studentNumber", userInfo.studentNumber);
            
            axios.post('/auth/admin/2fa/setup', bodyFormData, {
                headers: {'content-type': 'application/x-www-form-urlencoded'}
            })
            .then(response => {
                setQrCode(response.data.qrCodeUrl || response.data.qrCode);
                setShow2FAModal(true);
            })
            .catch(err => {
                setError('Error setting up 2FA: ' + (err.response?.data?.message || err.message));
            });
        } else {
            // User setting up their own 2FA
            const token = localStorage.getItem('token');
            axios.post('/auth/2fa/setup', {}, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            })
            .then(response => {
                setQrCode(response.data.qrCodeUrl || response.data.qrCode);
                setShow2FAModal(true);
            })
            .catch(err => {
                setError('Error setting up 2FA: ' + (err.response?.data?.message || err.message));
            });
        }
    };
    
    const handleConfirm2FA = () => {
        const bodyFormData = new FormData();
        bodyFormData.append("code", twoFACode);
        
        if (targetUserId && isAdmin) {
            // Admin confirming 2FA for another user
            bodyFormData.append("studentNumber", userInfo.studentNumber);
            
            axios.post('/auth/admin/2fa/verify', bodyFormData, {
                headers: {'content-type': 'application/x-www-form-urlencoded'}
            })
            .then(response => {
                if (response.data.valid) {
                    setSuccess('2FA has been successfully enabled!');
                    setShow2FAModal(false);
                    setTwoFACode('');
                    loadUserData(targetUserId);
                } else {
                    setError('Invalid verification code. Please try again.');
                }
            })
            .catch(err => {
                setError('Error confirming 2FA: ' + (err.response?.data?.message || err.message));
            });
        } else {
            // User confirming their own 2FA
            const token = localStorage.getItem('token');
            axios.post('/auth/2fa/verify', bodyFormData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'content-type': 'application/x-www-form-urlencoded'
                }
            })
            .then(response => {
                if (response.data.valid && response.data.enabled) {
                    setSuccess('2FA has been successfully enabled!');
                    setShow2FAModal(false);
                    setTwoFACode('');
                    loadUserData(targetUserId);
                } else {
                    setError('Invalid verification code. Please try again.');
                }
            })
            .catch(err => {
                setError('Error confirming 2FA: ' + (err.response?.data?.message || err.message));
            });
        }
    };
    
    const handleDisable2FA = () => {
        if (window.confirm('Are you sure you want to disable 2FA? This will make your account less secure.')) {
            if (targetUserId && isAdmin) {
                // Admin disabling 2FA for another user
                const bodyFormData = new FormData();
                bodyFormData.append("studentNumber", userInfo.studentNumber);
                
                axios.post('/auth/admin/2fa/disable', bodyFormData, {
                    headers: {'content-type': 'application/x-www-form-urlencoded'}
                })
                .then(() => {
                    setSuccess('2FA has been disabled.');
                    loadUserData(targetUserId);
                })
                .catch(err => {
                    setError('Error disabling 2FA: ' + (err.response?.data?.message || err.message));
                });
            } else {
                // User disabling their own 2FA - requires password
                const password = prompt('Please enter your password to disable 2FA:');
                if (!password) {
                    return; // User cancelled
                }
                
                const bodyFormData = new FormData();
                bodyFormData.append("password", password);
                
                axios.post('/auth/2fa/disable', bodyFormData, {
                    headers: {'content-type': 'application/x-www-form-urlencoded'}
                })
                .then(() => {
                    setSuccess('2FA has been disabled.');
                    loadUserData(targetUserId);
                })
                .catch(err => {
                    setError('Error disabling 2FA: ' + (err.response?.data?.message || err.message));
                });
            }
        }
    };
    
    const handleResetPassword = () => {
        if (!targetUserId) return;
        
        if (window.confirm(`Are you sure you want to reset the password for this user to 'alpine'?`)) {
            tableDataModules.resetPassword(userInfo.studentNumber)
                .then(() => {
                    setSuccess('Password has been reset to "alpine"');
                })
                .catch(err => {
                    setError('Error resetting password: ' + (err.response?.data?.message || err.message));
                });
        }
    };
    
    function submit(data) {
        setError('');
        setSuccess('');
        
        const userId = targetUserId || localStorage.getItem("id");
        const updateData = {
            firstName: data.firstName,
            lastName: data.lastName,
            year: data.year,
            email: data.email,
            phone: countryCode + data.phoneNumber,
            fulltime: data.fulltime
        };
        
        // Only include password if it's provided
        if (data.password) {
            updateData.newPassword = data.password;
        }
        
        // Always use tableDataModules.updateStudent for consistency and proper URL encoding
        const studentNumber = userInfo?.studentNumber;
        if (!studentNumber) {
            setError('Student number not found');
            return;
        }
        
        tableDataModules.updateStudent(studentNumber, updateData)
            .then(() => {
                setSuccess('Settings updated successfully!');
                loadUserData(targetUserId);
            })
            .catch(err => {
                setError('Error updating settings: ' + (err.response?.data?.message || err.message));
            });
    }
    
    if (loading) {
        return (
            <Container>
                <div className="text-center mt-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading user settings...</p>
                </div>
            </Container>
        );
    }
    
    return (
        <>
            <AuthCheck />
            <Container style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
                <Card>
                    <Card.Body>
                        <Card.Title className="text-center mb-4">
                            {targetUserId && isAdmin ? 
                                `User Settings - ${userInfo?.firstName} ${userInfo?.lastName}` : 
                                'My Profile Settings'
                            }
                        </Card.Title>
                        
                        {error && <Alert variant="danger">{error}</Alert>}
                        {success && <Alert variant="success">{success}</Alert>}
                        
                        <Form onSubmit={handleSubmit(submit)}>
                            <Row>
                                <Col md={6}>
                                    {/* First Name */}
                                    <Form.Group className="mb-3" controlId="formFirstName">
                                        <Form.Label>First Name *</Form.Label>
                                        <Form.Control 
                                            {...register("firstName", { required: "First name is required" })} 
                                            type="text" 
                                            placeholder="Enter first name" 
                                        />
                                        {errors.firstName && <div className="text-danger mt-1">{errors.firstName.message}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    {/* Last Name */}
                                    <Form.Group className="mb-3" controlId="formLastName">
                                        <Form.Label>Last Name *</Form.Label>
                                        <Form.Control 
                                            {...register("lastName", { required: "Last name is required" })} 
                                            type="text" 
                                            placeholder="Enter last name" 
                                        />
                                        {errors.lastName && <div className="text-danger mt-1">{errors.lastName.message}</div>}
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Row>
                                <Col md={6}>
                                    {/* Year */}
                                    <Form.Group className="mb-3" controlId="formYear">
                                        <Form.Label>Year</Form.Label>
                                        <Form.Control 
                                            {...register("year")} 
                                            type="text" 
                                            placeholder="Enter year" 
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    {/* Full-time/Part-time */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Study Type</Form.Label>
                                        <div className="form-check form-switch mt-2">
                                            <Form.Check
                                                {...register("fulltime")}
                                                type="switch"
                                                id="fulltimeSwitch"
                                                label="Full-Time"
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            {/* Email */}
                            <Form.Group className="mb-3" controlId="formEmail">
                                <Form.Label>Email Address *</Form.Label>
                                <Form.Control 
                                    {...register("email", { 
                                        required: "Email is required",
                                        validate: validateEmail
                                    })} 
                                    type="email" 
                                    placeholder="Enter email address" 
                                />
                                {errors.email && <div className="text-danger mt-1">{errors.email.message}</div>}
                            </Form.Group>
                            
                            {/* Phone Number */}
                            <Form.Group className="mb-3" controlId="formPhoneNumber">
                                <Form.Label>Phone Number *</Form.Label>
                                <InputGroup>
                                    <Form.Select 
                                        value={countryCode} 
                                        onChange={(e) => setCountryCode(e.target.value)}
                                        style={{ maxWidth: '150px' }}
                                    >
                                        {countryOptions.map((country) => (
                                            <option key={country.code} value={country.code}>
                                                {country.flag} {country.code}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control 
                                        {...register("phoneNumber", { 
                                            required: "Phone number is required",
                                            validate: validatePhone
                                        })} 
                                        type="tel" 
                                        placeholder="123456789" 
                                    />
                                </InputGroup>
                                {errors.phoneNumber && <div className="text-danger mt-1">{errors.phoneNumber.message}</div>}
                            </Form.Group>
                            
                            {/* Password */}
                            <Form.Group className="mb-3" controlId="formPassword">
                                <Form.Label>New Password (leave blank to keep current)</Form.Label>
                                <Form.Control 
                                    {...register("password", { validate: validatePassword })} 
                                    type="password" 
                                    placeholder="Enter new password" 
                                />
                                {password && (
                                    <div className="mt-2">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <small>Password Strength: <span className={`text-${passwordStrength.color}`}>{passwordStrength.text}</span></small>
                                        </div>
                                        <div className="progress" style={{ height: '5px' }}>
                                            <div 
                                                className={`progress-bar bg-${passwordStrength.color}`}
                                                style={{ width: `${passwordStrength.strength}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}
                                {errors.password && <div className="text-danger mt-1">{errors.password.message}</div>}
                            </Form.Group>
                            
                            {/* Confirm Password - only show if password is entered */}
                            {password && (
                                <Form.Group className="mb-3" controlId="formPasswordConfirm">
                                    <Form.Label>Confirm New Password *</Form.Label>
                                    <Form.Control 
                                        {...register("passwordConfirm", {
                                            required: password ? "Please confirm your password" : false,
                                            validate: value => !password || value === getValues().password || "Passwords do not match"
                                        })}
                                        type="password" 
                                        placeholder="Confirm new password" 
                                    />
                                    {errors.passwordConfirm && <div className="text-danger mt-1">{errors.passwordConfirm.message}</div>}
                                </Form.Group>
                            )}
                            
                            {/* 2FA Section */}
                            <hr />
                            <h5>Two-Factor Authentication</h5>
                            <div className="mb-3">
                                <p className="text-muted">
                                    Current Status: <strong>{userInfo?.['2faEnabled'] || userInfo?.twoFAEnabled ? 'Enabled' : 'Disabled'}</strong>
                                </p>
                                {(userInfo?.['2faEnabled'] || userInfo?.twoFAEnabled) ? (
                                    <Button variant="warning" onClick={handleDisable2FA}>
                                        Disable 2FA
                                    </Button>
                                ) : (
                                    <Button variant="success" onClick={handleSetup2FA}>
                                        Enable 2FA
                                    </Button>
                                )}
                            </div>
                            
                            {/* Admin-only actions */}
                            {targetUserId && isAdmin && (
                                <>
                                    <hr />
                                    <h5>Admin Actions</h5>
                                    <div className="mb-3">
                                        <Button variant="danger" onClick={handleResetPassword} className="me-2">
                                            Reset Password to 'alpine'
                                        </Button>
                                    </div>
                                </>
                            )}
                            
                            <div className="d-grid">
                                <Button variant="primary" type="submit" size="lg">
                                    Update Settings
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
            
            {/* 2FA Setup Modal */}
            <Modal show={show2FAModal} onHide={() => setShow2FAModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Set Up Two-Factor Authentication</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-3">
                        <p>Scan this QR code with your authenticator app:</p>
                        {qrCode && <img src={qrCode} alt="2FA QR Code" className="img-fluid" />}
                    </div>
                    <Form.Group className="mb-3">
                        <Form.Label>Enter the 6-digit code from your authenticator app:</Form.Label>
                        <Form.Control 
                            type="text" 
                            placeholder="000000"
                            value={twoFACode}
                            onChange={(e) => setTwoFACode(e.target.value)}
                            maxLength="6"
                            className="text-center"
                            style={{ fontSize: '1.2em', letterSpacing: '0.2em' }}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShow2FAModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleConfirm2FA}>
                        Confirm & Enable 2FA
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default UserSettings;